import { z } from 'zod';


/**
 * Schema for employee contract creation form
 * Validates all 12 required fields according to ASIS-3870 requirements
 */
export const createEmployeeContractSchema = z
  .object({
    // Contract basic info
    contractNumber: z
      .string()
      .trim()
      .min(1, 'employee:contracts.validation.contractNumberRequired')
      .refine(
        (value) => {
          const numericValue = Number(value);
          return !Number.isNaN(numericValue) && numericValue > 0 && Number.isInteger(numericValue);
        },
        {
          message: 'employee:contracts.validation.contractNumberPositive',
        },
      ),
    contractName: z
      .string()
      .trim()
      .min(1, 'employee:contracts.validation.contractNameRequired')
      .max(200, 'employee:contracts.validation.contractNameMaxLength'),
    contractStatusId: z
      .number({
        required_error: 'employee:contracts.validation.contractStatusRequired',
        invalid_type_error: 'employee:contracts.validation.contractStatusRequired',
      })
      .int()
      .positive(),
    contractTypeId: z
      .number({
        required_error: 'employee:contracts.validation.contractTypeRequired',
        invalid_type_error: 'employee:contracts.validation.contractTypeRequired',
      })
      .int()
      .positive(),

    // Dates (as strings in yyyy-mm-dd format from DatePicker)
    dateEntry: z
      .string({
        required_error: 'employee:contracts.validation.dateEntryRequired',
        invalid_type_error: 'employee:contracts.validation.dateEntryRequired',
      })
      .min(1, 'employee:contracts.validation.dateEntryRequired')
      .refine(
        (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      ),
    contractStartDate: z
      .string({
        required_error: 'employee:contracts.validation.contractStartDateRequired',
        invalid_type_error: 'employee:contracts.validation.contractStartDateRequired',
      })
      .min(1, 'employee:contracts.validation.contractStartDateRequired')
      .refine(
        (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      ),
    contractEndDate: z
      .string()
      .refine(
        (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value),
        { message: 'employee:contracts.validation.invalidDateFormat' },
      )
      .nullable()
      .optional()
      .transform((value) => (value === '' || !value ? null : value)),

    // Organization
    contractModalityId: z
      .number({
        required_error: 'employee:contracts.validation.contractModalityRequired',
        invalid_type_error: 'employee:contracts.validation.contractModalityRequired',
      })
      .int()
      .positive(),
    companyContractId: z
      .number({
        required_error: 'employee:contracts.validation.companyContractRequired',
        invalid_type_error: 'employee:contracts.validation.companyContractRequired',
      })
      .int()
      .positive(),
    areaId: z
      .number({
        required_error: 'employee:contracts.validation.areaRequired',
        invalid_type_error: 'employee:contracts.validation.areaRequired',
      })
      .int()
      .positive(),
    positionId: z
      .number({
        required_error: 'employee:contracts.validation.positionRequired',
        invalid_type_error: 'employee:contracts.validation.positionRequired',
      })
      .int()
      .positive(),
    costCenterId: z
      .number({
        required_error: 'employee:contracts.validation.costCenterRequired',
        invalid_type_error: 'employee:contracts.validation.costCenterRequired',
      })
      .int()
      .positive(),
    unionCode: z
      .string({
        required_error: 'employee:contracts.validation.unionRequired',
        invalid_type_error: 'employee:contracts.validation.unionRequired',
      })
      .min(1, 'employee:contracts.validation.unionRequired'),
  })
  .superRefine((data, context) => {
    // Validate dates: contractEndDate >= contractStartDate
    if (data.contractEndDate && data.contractStartDate) {
      const startDate = new Date(data.contractStartDate);
      const endDate = new Date(data.contractEndDate);

      if (endDate < startDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contractEndDate'],
          message: 'employee:contracts.validation.endDateAfterStartDate',
        });
      }
    }

    // Validate: dateEntry <= contractStartDate (fecha ingreso no posterior a inicio contrato)
    if (data.dateEntry && data.contractStartDate) {
      const entryDate = new Date(data.dateEntry);
      const startDate = new Date(data.contractStartDate);

      if (entryDate > startDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateEntry'],
          message: 'employee:contracts.validation.entryDateBeforeStartDate',
        });
      }
    }

    // Validate: dateEntry <= contractEndDate (fecha ingreso no puede ser mayor que término)
    if (data.contractEndDate && data.dateEntry) {
      const entryDate = new Date(data.dateEntry);
      const endDate = new Date(data.contractEndDate);

      if (entryDate > endDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dateEntry'],
          message: 'employee:contracts.validation.entryDateBeforeEndDate',
        });
      }
    }
  });

export type CreateEmployeeContractFormData = z.infer<typeof createEmployeeContractSchema>;
